import React, { useContext } from 'react';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';

import { FormContext, OrganisationContext } from 'contexts';

import { Button, Loader } from 'components/common';

import styles from './FormNavigation.module.scss';
import { ISaveDraftRequest } from 'services/draftService';
import { AuFormGroup } from 'components/DTA';
import { IFormAlertType } from 'types/forms/formTypes';

interface IFormNavigationProps {
  nextButtonText?: string;
  backButtonText?: string;
  hidePrevious?: boolean;
  hideNext?: boolean;
  showSaveAndExit?: boolean;
  disableNextButton?: boolean;
  textBlurb?: string;
  saveDraftName?: string;
  triggerFormValidation?: boolean;
  submissionInProgress?: boolean;

  onPreviousAction?: () => void;
  onNextAction?: () => void;
}

const FormNavigation: React.FC<IFormNavigationProps> = ({ nextButtonText, backButtonText, onPreviousAction, onNextAction, textBlurb, saveDraftName, hidePrevious = false, hideNext = false, showSaveAndExit = true, disableNextButton = false, triggerFormValidation = false, submissionInProgress = false }) => {
  const { currentStepIndex, handleBack, steps, saveDraft, setOnChangeValidation, setOnBlurValidation, setFormAlert } = useContext(FormContext);
  const { organisationDetailsResponse } = useContext(OrganisationContext);
  const { values, submitForm, setSubmitting, isSubmitting, validateForm } = useFormikContext();

  const handleNext = async () => {
    setSubmitting(true);

    if (triggerFormValidation) {
      // Validate the form
      const validationErrors = await validateForm();

      // No errors? submit the form else show form alert and turn on onchange and onblur validation
      if (!validationErrors || isEmpty(validationErrors)) {
        setOnChangeValidation(false);
        setOnBlurValidation(false);
        await submitForm();
      }
      else {
        const alert: IFormAlertType = {
          as: 'error',
          title: 'There was a problem with the form',
          message: 'Please check the form below and try again'
        };
        setFormAlert(alert);
        setOnChangeValidation(true);
        setOnBlurValidation(true);
        window.scrollTo(0, 0);
      }
    }
    else {
      await submitForm();
    }
    setSubmitting(false);
  };

  const saveAndExitClick = () => {
    window.scrollTo(0, 0);
    if (saveDraftName) {
      const saveRequest: ISaveDraftRequest = {
        organisationId: organisationDetailsResponse ? organisationDetailsResponse?.value?.value[0]?.accountid : '',
        name: saveDraftName,
        formData: JSON.stringify(values)
      };
      saveDraft(saveRequest);
    }
  };

  // TODO: Need a lint rule for handling these nicely
  /* eslint-disable @typescript-eslint/indent */
  const nextText = nextButtonText ? nextButtonText
    : currentStepIndex < steps.length - 1 ? 'Next'
      : 'Submit';
  /* eslint-enable @typescript-eslint/indent */

  return (
    <div className={styles.formNavigation}>
      {
        currentStepIndex > 0 && hidePrevious === false &&
        <Button as='secondary' type='button' onClick={onPreviousAction ?? handleBack}>{backButtonText ? backButtonText : 'Previous'}</Button>
      }
      {
        !hideNext &&
        <>
          {
            isSubmitting || submissionInProgress
              ? <Loader message='Working. Please wait.....' />
              : <Button as='primary' onClick={onNextAction ?? handleNext} disabled={disableNextButton}>{nextText}</Button>
          }
        </>
      }
      {
        textBlurb &&
        <AuFormGroup>
          <p>{textBlurb}</p>
        </AuFormGroup>
      }
      {
        showSaveAndExit &&
        <>
          <hr />
          <Button as='secondary' onClick={saveAndExitClick}>Save and exit Application</Button>
        </>
      }
    </div>
  );
};

export default FormNavigation;